import { Flex, Text } from '@chakra-ui/react';
import { faPlus, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ButtonText, FormFeedback, OutlineCard } from '@morressier/alembic';
import { useEffect, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { FieldLabel } from 'src/components/Field';
import { FormFieldFundingGeneric } from '../../../../../../../../domain/types/article';
import { get } from 'lodash';
import FunderSearch, { IFunder } from './FunderSearch';
import { FundingGrantNumbers, IGrantNumbers } from './Funding';
import { FundingGenericRadioGroup } from 'src/components/FundingGenericRadioGroup';
import { IDependentFundingGenericValue } from '@publishing-engine/domain/models';

export type IFundingGenericValue = {
  metadata: {
    fundingGeneric: IDependentFundingGenericValue<
      {
        fundingBody: string;
        grantNumber: string;
      }[]
    >;
  };
};

const FundingGeneric = ({
  config,
  onGrantNumbersInvalidChange
}: {
  config: FormFieldFundingGeneric;
  onGrantNumbersInvalidChange?: (isInvalid: boolean) => void;
}) => {
  const {
    formState: { errors },
    control,
    register,
    watch,
    clearErrors,
    setValue
  } = useFormContext<IFundingGenericValue>();
  const {
    params: { validations, labels },
    caseDataFields: { main: field, dependsOn }
  } = config;
  const mainField = `${field}.value` as const;
  const error = get(errors, field);
  const [invalidGrantNumbersByRow, setInvalidGrantNumbersByRow] = useState<
    Record<string, boolean>
  >({});
  const funders = watch(mainField);
  const exists = watch(field)?.exists;
  const isFunded = exists === 'externally funded';

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: mainField
  });

  const handleGrantNumberChange = (
    grantNumbers: IGrantNumbers,
    index: number
  ) => {
    const grantNumber =
      grantNumbers
        ?.map(({ text }) => text.trim())
        .filter(Boolean)
        .join(',') || '';
    setValue(`${mainField}.${index}.grantNumber`, grantNumber, {
      shouldValidate: !!grantNumbers?.length
    });
  };

  const handleGrantNumberValidityChange = (
    rowId: string,
    isInvalid: boolean
  ) => {
    setInvalidGrantNumbersByRow(prev => ({
      ...prev,
      [rowId]: isInvalid
    }));
  };

  const addFundingRow = () => {
    clearErrors(field);
    append({ fundingBody: '', grantNumber: '' });
  };

  useEffect(() => {
    onGrantNumbersInvalidChange?.(
      Object.values(invalidGrantNumbersByRow).some(Boolean)
    );
  }, [invalidGrantNumbersByRow, onGrantNumbersInvalidChange]);

  useEffect(() => {
    if (!isFunded) {
      setInvalidGrantNumbersByRow({});
      onGrantNumbersInvalidChange?.(false);
    }
  }, [isFunded, onGrantNumbersInvalidChange]);

  if (isFunded && !fields.length) {
    addFundingRow();
  }

  useEffect(() => {
    // Clear the funding information when 'isFunded' is false
    if (!isFunded) {
      setValue(mainField, []); // Set the funders array to an empty array
      clearErrors(mainField);
    }
  }, [isFunded, setValue, clearErrors, mainField]);

  return (
    <Flex flexDirection="column" gap={8} alignItems="flex-start" width="100%">
      {/* <Flex flexDirection="column" gap={2}>
        <Text textStyle="headingGroup" data-cy="funding-section-title">
          {submissionPreferences?.funding.mainLabel ??
            'Is there Funding to report for this Submission?'}
        </Text>
        <Text textStyle="textBody" data-cy="funding-section-explanation">
          {submissionPreferences?.fundingGeneric?.secondaryLabel}
        </Text>
      </Flex> */}
      <OutlineCard padding={4} width="100%">
        <Flex flexDirection="column" gap={4} alignItems="flex-start">
          <FieldLabel
            isRequired={validations.required}
            label={labels.main}
            subText={labels.subtitle}
          />

          <Flex flexDirection="column" gap={4}>
            <FundingGenericRadioGroup
              name={field}
              control={control}
              options={Object.entries(labels.options).map(([key, value]) => ({
                value: key,
                children: value.main
              }))}
            />
          </Flex>

          {isFunded ? (
            <>
              <Text textStyle="headingItem" data-cy="funder-list-title">
                Funder List
              </Text>
              {/* --- Funding rows --- */}

              {fields.map((field, index) => (
                <OutlineCard
                  key={field.id}
                  bgColor={'surfaceSecondaryFill'}
                  padding={4}
                  width="100%"
                >
                  <Flex flexDirection="column" gap={2} width="100%">
                    <FieldLabel
                      isRequired
                      label={labels.section.title}
                      subText={labels.section.explanation}
                      helpText={labels.helpText}
                    />
                    <Flex gap={6} flexDirection="column">
                      <Flex flexDirection="column" gap={2}>
                        <FunderSearch
                          {...register(`${mainField}.${index}.fundingBody`, {
                            validate: value =>
                              (value && value.trim().length > 0) ||
                              'Funding Body is required'
                          })}
                          isInvalid={!!error?.value?.[index]?.fundingBody}
                          placeholder="Search for funders"
                          onFunderSelect={(funder: IFunder) => {
                            update(index, {
                              ...field,
                              fundingBody: funder.name.trim()
                            });
                          }}
                          aria-label="Funder search"
                          onBlur={e => {
                            const trimmed = e.target.value.trim();
                            setValue(
                              `${mainField}.${index}.fundingBody`,
                              trimmed,
                              {
                                shouldValidate: true
                              }
                            );
                          }}
                          onAddUnverifiedFunder={() => {
                            clearErrors(`${mainField}.${index}.fundingBody`);
                          }}
                          data-cy={`funder-search-${index}`}
                        />

                        {error?.value?.[index]?.fundingBody && (
                          <FormFeedback variant="error">
                            {error?.value?.[index]?.fundingBody?.type ===
                            'manual'
                              ? error?.value?.[index]?.fundingBody?.message
                              : 'Funding Body is required'}
                          </FormFeedback>
                        )}
                      </Flex>

                      {funders?.[index]?.fundingBody?.trim() && (
                        <Flex flexDirection="column" gap={2}>
                          <FieldLabel
                            isRequired={false}
                            label="Grant or Award Numbers"
                            subText="Add grant or award numbers one by one: type and press Enter after each."
                            helpText={labels.helpText}
                          />

                          <FundingGrantNumbers
                          initialValues={
                              funders[index].grantNumber
                                ? funders[index].grantNumber
                                    .split(',')
                                    .map(v => ({ text: v }))
                                : undefined
                            }
                            onValueChange={grantNumbers =>
                              handleGrantNumberChange(grantNumbers, index)
                            }
                            onInvalidChange={isInvalid =>
                              handleGrantNumberValidityChange(field.id, isInvalid)
                            }
                            errorMessage={
                              !funders[index].grantNumber?.trim() &&
                              error?.value?.[index]?.grantNumber
                                ? 'Grant or Award Number is required'
                                : null
                            }
                          />
                        </Flex>
                      )}

                      <ButtonText
                        alignSelf={'flex-end'}
                        iconLeft={<FontAwesomeIcon icon={faTrash} />}
                        onMouseDown={e => e.preventDefault()}
                        onClick={() => {
                          setInvalidGrantNumbersByRow(prev => {
                            const next = { ...prev };
                            delete next[field.id];
                            return next;
                          });
                          remove(index);
                        }}
                        marginLeft="auto"
                        data-cy={`delete-funder-button-${index}`}
                      >
                        Delete Funder
                      </ButtonText>
                    </Flex>
                  </Flex>

                  {error?.['root'] ? (
                    <FormFeedback
                      variant="error"
                      data-cy="funding-error-message"
                    >
                      Please provide funding data to proceed.
                    </FormFeedback>
                  ) : null}
                </OutlineCard>
              ))}

              <Flex alignItems="center" width="100%">
                <ButtonText
                  alignSelf={'flex-end'}
                  iconLeft={<FontAwesomeIcon icon={faPlus} />}
                  onClick={addFundingRow}
                  marginLeft="auto"
                  data-cy="add-funder-button"
                >
                  Add {fields.length > 0 ? 'another' : ''} fund
                </ButtonText>
              </Flex>
            </>
          ) : null}
        </Flex>
        {error?.[dependsOn] ? (
          <FormFeedback variant="error">
            Please select a funding option to continue
          </FormFeedback>
        ) : null}
      </OutlineCard>
    </Flex>
  );
};

export default FundingGeneric;
